import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import { getMcode } from "src/store/mcode";
import UserValidationSchema from 'src/validation/UserValidationSchema';
import { ValidationMessages } from 'src/components/common/ValidationMessage';
import { format } from "date-fns";
import { deleteUser } from "src/store/user";
import { resetSingleUser, resetStateIdele } from 'src/store/user';
import { useAuth } from 'src/hooks/useAuth';

const UserForm = (props) => {
  const {
    id = 0,
    isEditScreen ,
    isInputScreen ,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  
  const { user: userData } = useAuth();
  const auth_Cd = parseInt(userData.auth_cd);
  const dispatch = useDispatch();
  const [icon, setIcon] = useState('');
  const [iconMenuVisible, setIconMenuVisible] = useState(false);
  const [validationMessages, setValidationMessages] = useState([]);
  const [selectedIcon, setSelectedIcon] = useState(icon);
  const [fromEdit, setFromEdit] = useState(isEditScreen);
  const [fromInput, setFromInput] = useState(isInputScreen);
  const [isReturnConfirm, setIsReturnConfirm] = useState(false);

  const user = useSelector((state) => state.user.singleData);
  const statusCd = useSelector((state) => state.mcode.data.status);
  const authCd = useSelector((state) => state.mcode.data.auth);
  const departmentCd = useSelector((state) => state.mcode.data.department);
  const workingHoursCd = useSelector((state) => state.mcode.data.workinghourscd);
  const userStatus = useSelector((state) => state.user.status);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(UserValidationSchema),
    mode: 'onSubmit',
    defaultValues: {
      id: id,
      changePassword: id ? false : true,
    }
  });

  const iconNum = watch('icon_num');
  const onSubmit = (data) => {
    const formattedHireDate = format(data.hire_date, "yyyy-MM-dd")
    const formattedRetirementDate = data.retirement_date ? format(data.retirement_date, "yyyy-MM-dd") : null;
    const formatedData = {
      ...data,
      hire_date: formattedHireDate,
      retirement_date: formattedRetirementDate
    }
    navigate("/userConfirm", { state: { formData: formatedData ,ShowProfileScreen:fromInput ? fromInput : fromEdit  } });
  };

  const clickBackBtn = () => {
    navigate("/userList");
  };

  const clickDeleteBtn = async () => {
    const userConfirmed = window.confirm("Are you sure you want to delete?");
    if (!userConfirmed) {
      return; // Exit if the user cancels the confirmation
    }
  
    const response = await dispatch(deleteUser(id));
    if (deleteUser.fulfilled.match(response)) {
      alert(response.payload.message);
      navigate("/userList");
    } else if (deleteUser.rejected.match(response)) {
      setValidationMessages(response.error.message);
    }
  };
  const icons = useMemo(() => [0, 1, 2, 3, 4, 6, 7, 8, 9, 11, 12, 14, 16, 17, 19, 21, 24, 28, 30, 31, 32, 33,
    34, 35, 36, 37, 38, 39, 40, 41, 42, 43
  ], []);

  // Handle icon click: Set icon in state and form value
  const handleIconClick = (selectedIcon) => {
    setIcon(selectedIcon);
    setValue("icon_num", selectedIcon); // Update the form value for icon_num
    setIconMenuVisible(false); // Close the menu after selection
  };

  // Handle outside click to close the menu
  const handleOutsideClick = (e) => {
    if (!e.target.closest("#iconmenu") && !e.target.closest(".jq-select-icon")) {
      setIconMenuVisible(false);
    }
  };
  useEffect(() => {
    if (location.state?.formData) {
      setIcon(location.state.formData.icon_num);
      reset(location.state.formData);
      setIsReturnConfirm(location.state.isInputConfirmScreen);
      setFromEdit(location.state.isInputConfirmScreen || isEditScreen);
    }
    else if (user && Object.keys(user).length !== 0) {
      reset(user);
    }
  }, [location.state, reset, user]);

  useEffect(() => {
    dispatch(getMcode({ section_cd: 'jobtype,department,position,worklocation,status,auth,workinghourscd,usertype' }));

    return () => {
      dispatch(resetSingleUser());
    };
  }, []);

  useEffect(() => {
    // Listen for outside clicks to close the icon menu
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const messagesArray = Object.values(errors).map((error) => error.message);
    setValidationMessages(messagesArray);
  }, [errors]);

  useEffect(() => {
    if (validationMessages.length > 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [validationMessages]);

  useEffect(() => {
    if (userStatus === 'succeeded') {
      dispatch(resetStateIdele());
    }
  }, [userStatus]);

  useEffect(() => {
    if (iconNum) {
      setSelectedIcon(iconNum); // Update selected icon when icon_num changes
    }
  }, [iconNum]);

  const changePassword = watch('changePassword');

  return (
    <div>
      <form className="form-horizontal" onSubmit={handleSubmit(onSubmit)}>
        <input type="hidden" {...register("id")} />
        {validationMessages && validationMessages.length > 0 && (
          <div style={{ background: "#ffe4e2" }}>
            <ValidationMessages
              messages={validationMessages}
              onClose={() => setValidationMessages([])}
            />
          </div>
        )}
        {/* Icon Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Icon *
          </label>
          <div className="col-sm-9">
            {/* Icon display */}
            <span
              id="user-icon"
              className={`statusicon sta-icon${selectedIcon} jq-select-icon`} // Dynamically set the icon class
              onClick={() => setIconMenuVisible(!iconMenuVisible)}
            ></span>

            {/* Hidden input for icon_num */}
            <input
              type="hidden"
              {...register("icon_num")}
              value={selectedIcon} // Bind selected icon to the hidden input value
            />
          </div>
        </div>

        {/* LoginID Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            LoginID *
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control"
              maxLength={256}
              {...register("login_id")}
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Password *
          </label>
          <div className="col-sm-9">
            <input
              style={{ width: "50%", display: "inline-block" }}
              type="password"
              disabled={(!changePassword && id)}
              className="form-control"
              maxLength={256}
              {...register("password")}
            />
            {(id !== 0) &&
              (<>
                <input type="checkbox" id={`change_password${id}`} style={{ marginLeft: 20 }}  {...register("changePassword")} />
                <label htmlFor={`change_password${id}`} style={{ marginLeft: 10 }}>Change password</label>
              </>)}
          </div>
        </div>

        {/* Name Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Name *
          </label>
          <div className="col-sm-9">
            <input
              type="text"
              className="form-control"
              maxLength={100}
              {...register("name")}
            />
          </div>
        </div>

        {/* Email Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Mail Address *
          </label>
          <div className="col-sm-9">
            <input
              type="email"
              className="form-control"
              maxLength={256}
              {...register("email")}
            />
          </div>
        </div>

        {/* Status Radio Buttons */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>Status *</label>
          <div className="col-sm-9">
            {statusCd && statusCd.map((status_cd, index) => (
              <React.Fragment key={index}>
                <input
                  {...register("status_cd")}
                  id={`status_cd_${index}`}
                  type="radio"
                  value={status_cd.cd}
                />
                <label
                  htmlFor={`status_cd_${index}`}
                  style={{ fontWeight: "normal", marginLeft: 5, marginRight: 5 }}
                >
                  {status_cd.code_value1}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Authority Radio Buttons */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>Authority *</label>
          <div className="col-sm-9">
            {authCd && authCd.map((auth_cd, index) => (
              <React.Fragment key={index}>
                <input
                  {...register("auth_cd")}
                  id={`auth_cd_${index}`}
                  type="radio"
                  value={auth_cd.cd}
                  disabled={auth_Cd !== 1 && id !== null }
                />
                <label
                  htmlFor={`auth_cd_${index}`}
                  style={{ fontWeight: "normal", marginLeft: 5, marginRight: 5 }}
                >
                  {auth_cd.code_value1}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>
      
        {/* Department Select */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>Department *</label>
          <div className="col-sm-9">        
          <select 
            name="department_cd" 
            className="form-control"
            value={user?.department_cd?.toString() || ""}
            {...register("department_cd")}
          >
            <option value="">-----</option>
            {departmentCd && departmentCd.slice(1).map((department_cd) => (
              <option
                key={department_cd.cd}
                value={department_cd.cd}
              >
                {department_cd.code_value1}
              </option>
            ))}
          </select>
          </div>
        </div>

        {/* Core time Radio Buttons */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>Core time *</label>
          <div className="col-sm-9">
            {workingHoursCd && workingHoursCd.map((working_hours_cd, index) => (
              <React.Fragment key={index}>
                <input
                  {...register("working_hours_cd")}
                  id={`working_hours_cd_${index}`}
                  type="radio"
                  value={working_hours_cd.cd}
                />
                <label
                  htmlFor={`working_hours_cd_${index}`}
                  style={{ fontWeight: "normal", marginLeft: 5, marginRight: 5 }}
                >
                  {working_hours_cd.code_value1}
                </label>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Start date Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Hire date *
          </label>
          <div className="col-sm-9">
            <input
              type="date"
              className="form-control"
              {...register("hire_date")}
            />
          </div>
        </div>

        {/* Retirement date Field */}
        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
            Retirement date
          </label>
          <div className="col-sm-9">
            <input
              type="date"
              className="form-control"
              {...register("retirement_date")}
            />
          </div>
        </div>

        {/* Icon selection menu */}
        {iconMenuVisible && (
          <div id="iconmenu" className="icon_menu" style={{ width: 211 }}>
            <dt className="iconmenu_title">Select Icon</dt>
            <dd>
              <ul className="iconmenu_list">
                {icons.map((item) => (
                  <li
                    key={item}
                    className={`iconbox statusicon sta-icon${item} ${item === icon ? "now_icon" : ""}`}
                    onClick={() => handleIconClick(item)}
                  ></li>
                ))}
              </ul>
            </dd>
          </div>
        )} {(fromEdit || isReturnConfirm) && (
          <div className="user-profile-section">
            <h3 className="title-style01">User Profile</h3>
            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
                Date of Birth 
              </label>
              <div className="col-sm-9">
                <input
                  type="date"
                  className="form-control"
                  {...register("dateOfBirth")}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
                Blood Group 
              </label>
              <div className="col-sm-9">
                <select className="form-control" {...register("bloodGroup")}>
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
                Hobbies 
              </label>
              <div className="col-sm-9">
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter your hobbies..."
                  {...register("hobbies")}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
                Education History 
              </label>
              <div className="col-sm-9">
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Enter your education history..."
                  {...register("educationHistory")}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="col-sm-3 control-label" style={{ textAlign: "end", marginTop: 6 }}>
                Known Languages 
              </label>
              <div className="col-sm-9">
                <select
                  className="form-control"
                  multiple
                  {...register("knownLanguages")}
                >
                  <option value="javascript">JavaScript</option>
                  <option value="typescript">TypeScript</option>
                  <option value="python">Python</option>
                  <option value="java">Java</option>
                  <option value="csharp">C#</option>
                  <option value="cpp">C++</option>
                  <option value="php">PHP</option>
                  <option value="ruby">Ruby</option>
                  <option value="swift">Swift</option>
                  <option value="go">Go</option>
                </select>
                <small className="text-muted">Hold Ctrl (Windows) or Cmd (Mac) to select multiple languages</small>
              </div>
            </div>
          </div>
        )}
        {/* Buttons */}
        <div className="form-group text-center" style={{ marginTop: 60 }}>
          <button type="button" className="btn btn-default btn-lg" style={{ margin: "0 10px" }} onClick={clickBackBtn}>
            Back
          </button>
          <button type="submit" className="btn btn-success btn-lg jq-confirm_data" style={{ margin: "0 10px" }}>
            Confirm
          </button>
        </div>
        {id ?
          <div className="form-group text-center">
            <button type="button" className="btn btn-default btn-lg" style={{ margin: "0 10px" }} onClick={clickDeleteBtn}>
              Delete
            </button>
          </div>
          : null
        }    </form>

    </div>
  );
};

export default UserForm;
